import { pick } from 'js/utils/ramda.util';
import React, { useMemo, useCallback } from 'react';
import { Loader } from 'js/components/common/Loader';
import { useFormatter } from 'js/hooks/useFormatter';
import { useComponentTranslations } from 'js/hooks/component/useComponentTranslations';

import { Games } from '../components/Games';
import { useDynamicReducerSelector } from '../store';

import style from '../FreeSpinsBalance.scss';

export const useTableData = () => {
    const formatter = useFormatter();
    const translate = useComponentTranslations();
    const {
       freeSpinsBalance, bonusDetails
    } = useDynamicReducerSelector(pick(['freeSpinsBalance', 'bonusDetails']));

    const formatCurrency = useCallback(
        ({ amount, currencyCode }) => formatter.formatCurrency(amount, { currencyCode }),
        [formatter]);

    const formatDate = useCallback(
        date => formatter.getDateTimeShort(formatter.parseImsDate(date)),
        [formatter]);

    const getLoader = useCallback((id) => {
        if (!bonusDetails[id]) {
            return [];
        }

        return bonusDetails[id].isLoading && <Loader className={style.loader}/> ;
    }, [bonusDetails]);

    const getSlide = useCallback((id) => {
        if (!bonusDetails[id]?.bonusTemplate?.gamingContext) {
            return [];
        }

        const { selectedGames } = bonusDetails[id].bonusTemplate.gamingContext;

        return [{
            id: 1,
            content: <Games selectedGames={selectedGames}/>,
            className: style.slider
        }];
    }, [bonusDetails]);

    const getAccordionRowList = useCallback((id) => {
        if (!bonusDetails[id]?.bonus) {
           return [];
        }

        const { pendingWinnings, endDate } = bonusDetails[id].bonus;

        return [
            ...endDate ? [{
                id: 1,
                content:
                    <>
                        <div className="accordionBodyCellName">{translate('expiryDate')}</div>
                        <div className="accordionBodyCellValue">{formatDate(endDate)}</div>
                    </>
            }] : [],
            {
                id: 2,
                content:
                    <>
                        <div className="accordionBodyCellName">{translate('pendingBonusWinnings')}</div>
                        <div className="accordionBodyCellValue">{formatCurrency(pendingWinnings)}</div>
                    </>
            }
        ];
    }, [translate, bonusDetails, formatCurrency, formatDate]);

    return useMemo(() => ({
        accordionView: {
            rows: freeSpinsBalance.map(balance => {
                const { name = '', referenceId, balance: { amount = '' } } = balance;

                return {
                    id: referenceId,
                    headerCells: [
                        {
                            id: 0,
                            content: name,
                            className: 'accordionHeaderSmallDetails'
                        },
                        {
                            id: 1,
                            content: `${amount} ${translate('spins')}`,
                            className: `${style.amount} accordionHeaderSmallDetails`
                        }
                    ],
                    rowCells: bonusDetails?.[referenceId]?.error
                        ? [{
                            id: 0,
                            content: <div className={style.accordionError}>{bonusDetails[referenceId].error}</div>
                          }]
                        : [
                            { id: 0, content: getLoader(referenceId) },
                            ...getSlide(referenceId),
                            ...getAccordionRowList(referenceId)
                        ]
                };
            })
        }
    }), [freeSpinsBalance, getAccordionRowList, getSlide, getLoader, bonusDetails, translate]);
};
